import React, { useEffect, useMemo, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useSearchParams } from 'react-router-dom';

import editIcon from 'assets/actionPlan/edit.svg';
import searchIcon from 'assets/actionPlan/search.svg';
import Button from 'common-ui/button';
import Input from 'common-ui/input';
import { AsyncSelect } from 'common-ui/select';
import Switch from 'common-ui/switch';
import Pagination from 'common-ui/table/Pagination';
import loadIcon from 'common-ui/table/loading.svg';
import dayjs from 'dayjs';
import EmptyView from 'pages/components/emptyView';

import {
  disableAction,
  enableAction,
  getActions,
  getAssignments,
} from '../../../features/actions/actionPlanActions';
import {
  clearActionsState,
  selectActionsList,
  selectActionsLoading,
  selectTotalElements,
  selectTotalPages,
  setActionFilters,
  setDashboardSearchData,
} from '../../../features/actions/actionPlanSlice';
import ActionEditModal from '../actionEditModal/ActionEditModal';
import CreateActionModal from '../createActionModal/CreateActionModal';
import {
  ActionHeader,
  Badge,
  CardBody,
  CardContainer,
  CardFooter,
  CardHeader,
  CardHeaderTitle,
  CardsGrid,
  Container,
  DateValue,
  EditBtn,
  EffectiveDateSection,
  EmptyWrapper,
  LoadContainer,
  LoadingIcon,
  StatItem,
  StatLabel,
  StatValue,
} from './ActionManagement.styles';

const ActionManagement = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const actions = useSelector(selectActionsList);
  const isLoading = useSelector(selectActionsLoading);
  const [isFocused, setIsFocused] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEffectiveDateModalOpen, setIsEffectiveDateModalOpen] = useState(false);
  const [selectedTargetId, setSelectedTargetId] = useState(null);
  const [editData, setEditData] = useState(null);
  const [earliestAssignmentDate, setEarliestAssignmentDate] = useState(null);
  const totalElements = useSelector(selectTotalElements);
  const totalPages = useSelector(selectTotalPages);
  const filters = useSelector((state) => state.actions.filters);

  const searchValue = filters.searchValue;
  const statusValue = filters.statusValue;
  const currentPage = filters.currentPage;
  const pageSize = filters.pageSize;
  const userType = localStorage.getItem('userType');
  const hasManagementAccess = userType === 'General Manager' || userType === 'Super Admin';
  const sortedActions = useMemo(() => {
    if (!searchValue.trim()) {
      return actions;
    }

    const searchLower = searchValue.trim().toLowerCase();

    return [...actions].sort((a, b) => {
      const aName = (a.pendingActionName || a.actionName).toLowerCase();
      const bName = (b.pendingActionName || b.actionName).toLowerCase();

      const aExact = aName === searchLower;
      const bExact = bName === searchLower;
      if (aExact && !bExact) return -1;
      if (!aExact && bExact) return 1;

      const aStarts = aName.startsWith(searchLower);
      const bStarts = bName.startsWith(searchLower);
      if (aStarts && !bStarts) return -1;
      if (!aStarts && bStarts) return 1;

      if ((aExact && bExact) || (aStarts && bStarts) || (!aStarts && !bStarts)) {
        return aName.length - bName.length;
      }

      return 0;
    });
  }, [actions, searchValue]);
  const handleStatusChange = (selectedOption) => {
    dispatch(
      setActionFilters({
        statusValue: selectedOption,
        currentPage: 1,
      })
    );
  };

  useEffect(() => {
    dispatch(clearActionsState());
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      getActions({
        page: currentPage - 1,
        size: pageSize,
        sort: 'id,desc',
        name: searchValue.trim(),
        enabled: statusValue?.value,
      })
    );
  }, [dispatch, currentPage, pageSize, searchValue, statusValue]);
  const handlePageChange = (page) => {
    dispatch(
      setActionFilters({
        currentPage: page,
      })
    );
  };

  const handleRowCountChange = (count) => {
    dispatch(
      setActionFilters({
        pageSize: count,
        currentPage: 1,
      })
    );
  };
  const handleSearch = (e) => {
    const value = e.target.value;
    dispatch(
      setActionFilters({
        searchValue: value,
        currentPage: 1,
      })
    );
  };

  const handleToggleEnable = async (id, currentEnabledStatus) => {
    const item = actions.find((a) => a.id === id);
    const hasAssignments = Number(item?.activeAssignmentsCount || 0) > 0;

    if (!currentEnabledStatus) {
      dispatch(enableAction(id))
        .unwrap()
        .then(() => refreshActions());
      return;
    }

    if (!hasAssignments) {
      dispatch(disableAction({ id }))
        .unwrap()
        .then(() => refreshActions());
      return;
    }

    try {
      const response = await dispatch(
        getAssignments({ actionId: id, page: 0, size: 1000, active: true })
      ).unwrap();

      const assignments = response?.content || [];

      if (assignments.length > 0) {
        const earliestDate = assignments.reduce((earliest, assignment) => {
          if (!assignment.effectiveDate) return earliest;
          const assignmentDate = dayjs(assignment.effectiveDate);
          return !earliest || assignmentDate.isBefore(earliest) ? assignmentDate : earliest;
        }, null);

        setEarliestAssignmentDate(earliestDate);
      } else {
        setEarliestAssignmentDate(null);
      }
    } catch (error) {
      console.error('Failed to fetch assignments:', error);
      setEarliestAssignmentDate(null);
    }

    setSelectedTargetId(id);
    setIsEffectiveDateModalOpen(true);
  };
  const updateTargetStatus = (effectiveDate) => {
    if (selectedTargetId) {
      return dispatch(disableAction({ id: selectedTargetId, effectiveDate }))
        .unwrap()
        .then(() => {
          refreshActions();
          setIsEffectiveDateModalOpen(false);
          setSelectedTargetId(null);
        })
        .catch(() => {});
    }
  };
  const handleEditClick = (item) => {
    setEditData(item);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditData(null);
  };

  const handleCardTitleClick = (actionId) => {
    navigate(`/action-plan/sales-target/${actionId}`);
  };
  const refreshActions = (page = currentPage) => {
    dispatch(
      getActions({
        page: page - 1,
        size: pageSize,
        sort: 'id,desc',
        name: searchValue.trim(),
        enabled: statusValue?.value === 'all' ? null : statusValue?.value,
      })
    );
  };

  return (
    <Container>
      <ActionHeader>
        <Input
          label="Search by name"
          placeholder=""
          width="400px"
          icon={isFocused || searchValue ? null : searchIcon}
          value={searchValue}
          onChange={handleSearch}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          clearable
          onClear={() =>
            dispatch(
              setActionFilters({
                searchValue: '',
                currentPage: 1,
              })
            )
          }
        />
        <AsyncSelect
          className="status-label"
          placeholder="Select status"
          width="200px"
          value={statusValue}
          onChange={handleStatusChange}
          defaultOptions={[
            { value: 'true', label: 'Active' },
            { value: 'false', label: 'Inactive' },
          ]}
          isClearable
        />
        {hasManagementAccess && (
          <Button
            onClick={() => {
              setEditData(null);
              setIsModalOpen(true);
            }}
          >
            + Create Action
          </Button>
        )}
      </ActionHeader>

      <CardsGrid $isEmpty={!isLoading && sortedActions?.length === 0}>
        {isLoading ? (
          <LoadContainer>
            <LoadingIcon src={loadIcon} alt="Loading..." />
          </LoadContainer>
        ) : sortedActions?.length === 0 ? (
          <EmptyWrapper>
            <EmptyView title={'No results found'} />
          </EmptyWrapper>
        ) : (
          sortedActions?.map((item) => (
            <CardContainer key={item.id}>
              <CardHeader>
                <CardHeaderTitle onClick={() => handleCardTitleClick(item.id)}>
                  {item.pendingActionName || item.actionName}
                </CardHeaderTitle>
                <Badge $active={item.enabled}>{item.enabled ? 'Active' : 'Inactive'}</Badge>
              </CardHeader>

              <CardBody>
                <StatItem>
                  <StatLabel>Assignments</StatLabel>
                  <StatValue>{item.activeAssignmentsCount || 0}</StatValue>
                </StatItem>
                <StatItem $right>
                  <StatLabel>Created Date</StatLabel>
                  <DateValue>
                    {item.createdAt ? dayjs(item.createdAt).format('YYYY-MM-DD') : '---'}
                  </DateValue>
                </StatItem>
              </CardBody>

              <CardFooter>
                {hasManagementAccess && (
                  <Switch
                    isOn={item.enabled}
                    onToggle={() => handleToggleEnable(item.id, item.enabled)}
                  />
                )}
                {item.enabled ? 'Enabled' : 'Disabled'}
                {hasManagementAccess && (
                  <EditBtn
                    src={editIcon}
                    alt="edit"
                    $disabled={!item.enabled}
                    onClick={() => item.enabled && handleEditClick(item)}
                  />
                )}
              </CardFooter>
              {(item.disableEffectiveDate || item.updateEffectiveDate) && (
                <EffectiveDateSection>
                  <StatLabel className="size-date">
                    {!item.enabled ? 'Disabled On:' : 'Effective Date:'}
                  </StatLabel>
                  <DateValue>
                    {dayjs(item.disableEffectiveDate || item.updateEffectiveDate).format(
                      'YYYY-MM-DD'
                    )}
                  </DateValue>
                </EffectiveDateSection>
              )}
            </CardContainer>
          ))
        )}
      </CardsGrid>
      <ActionEditModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        selectedAction={editData}
        onSuccess={refreshActions}
      />
      <CreateActionModal
        isOpen={isEffectiveDateModalOpen}
        isEffectiveDate={true}
        onClose={() => {
          setIsEffectiveDateModalOpen(false);
          setEarliestAssignmentDate(null);
        }}
        onConfirm={updateTargetStatus}
        earliestAssignmentDate={earliestAssignmentDate}
      />
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        handleRowCountChange={handleRowCountChange}
        dataCount={totalElements}
        count={pageSize}
        editableRowCount={true}
        pageOptions={[12, 24, 36]}
      />
    </Container>
  );
};

export default ActionManagement;
